import {
  Box,
  Button as ChakraButton,
  Center,
  Icon,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Modal, showToast, Text } from '@diamond/shared/ui';
import {
  adminCaptureException,
  formatCurrencyIdr,
  unixToHumanDate,
} from '@diamond/shared/utils';
import { getApiErrorMessage } from '@diamond/sol-admin/utils';
import ChevronRight from '@mui/icons-material/ChevronRight';
import type { ColDef } from 'ag-grid-community';
import { isAxiosError } from 'axios';
import { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  useCollectionMutationDetailMutation,
  useSearchLHCItemMutationsMutation,
} from '../../hooks';
import type {
  LHCDetailInvoiceRowData,
  SearchNomorMutasiApiItem,
  SearchNomorMutasiModalProps,
  SearchNomorMutasiRowData,
  SearchNomorMutasiSelectionPayload,
} from '../../types';
import { CollectionSearchResultsSection } from '../collection-search-results-section';
import {
  MutationContextSummary,
  MutationDetailDrawer,
  MutationSearchFilters,
} from './mutation-modal';

const MIN_SEARCH_LENGTH = 3;

type ApiErrorPayload = {
  message?: string;
  statusCode?: number;
  error?: string;
  [key: string]: unknown;
};

type CollectionMutationSentryContext = {
  lhc_no: string;
  lhc_id: string;
  mutation_no: string | null;
  mutation_id: string | null;
  item_id: string | null;
  order_meta_id: string | null;
  delivery_order_no: string | null;
  invoice_no: string | null;
  search_mutation_no?: string | null;
  search_memo_line?: string | null;
  amount_applied?: number | null;
};

function setTruthyTags(
  tags: Record<string, string | number | undefined>
): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(tags).filter(
      ([, value]) => value !== undefined && value !== ''
    )
  ) as Record<string, string | number>;
}

function captureCollectionMutationError({
  error,
  queryKey,
  operation,
  context,
}: {
  error: unknown;
  queryKey: string;
  operation: string;
  context: CollectionMutationSentryContext;
}) {
  const isAxios = isAxiosError<ApiErrorPayload>(error);
  const payloadData = isAxios ? error.response?.data : undefined;
  const statusCode =
    payloadData?.statusCode ?? (isAxios ? error.response?.status : undefined);
  const message =
    (typeof payloadData?.message === 'string' && payloadData.message) ||
    (error instanceof Error ? error.message : 'Unknown error');

  adminCaptureException({ message }, (scope) => {
    scope.setTags(
      setTruthyTags({
        queryKey,
        operation,
        lhc_no: context.lhc_no,
        lhc_id: context.lhc_id,
        mutation_no: context.mutation_no ?? undefined,
        mutation_id: context.mutation_id ?? undefined,
        status_code: statusCode,
      })
    );
    scope.setContext('collection_mutation', context);
    scope.setContext('error', {
      error: payloadData ?? message,
    });
    scope.setLevel('error');
  });
}

function buildRowDebugContext(
  rowData: LHCDetailInvoiceRowData | null
): Pick<
  CollectionMutationSentryContext,
  'item_id' | 'order_meta_id' | 'delivery_order_no' | 'invoice_no'
> {
  return {
    item_id: rowData?.rowId ?? null,
    order_meta_id: rowData?.orderMetaId ?? null,
    delivery_order_no: rowData?.deliveryOrderNo ?? null,
    invoice_no: rowData?.invoiceNo ?? null,
  };
}

function parseDateToUnixSeconds(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const ddmmyy = /^(\d{2})\/(\d{2})\/(\d{2})$/;
  const fullYearMatch = trimmed.match(ddmmyyyy);
  const shortYearMatch = trimmed.match(ddmmyy);
  const matched = fullYearMatch || shortYearMatch;

  if (matched) {
    const day = Number(matched[1]);
    const month = Number(matched[2]);
    const year =
      matched[3].length === 2 ? 2000 + Number(matched[3]) : Number(matched[3]);

    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
      return Math.floor(new Date(year, month - 1, day).getTime() / 1000);
    }
  }

  const asNumber = Number(trimmed);
  if (Number.isFinite(asNumber)) {
    return asNumber > 1_000_000_000_000
      ? Math.floor(asNumber / 1000)
      : Math.floor(asNumber);
  }

  const parsed = Date.parse(trimmed);
  if (Number.isNaN(parsed)) return undefined;

  return Math.floor(parsed / 1000);
}

function parseAmountToNumber(value: string): number | undefined {
  const normalized = value.replace(/[^\d]/g, '');
  if (!normalized) return undefined;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatDisplayAmount(value: string): string {
  const numeric = value.replace(/[^\d]/g, '');
  if (!numeric) return '';

  return numeric.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function normalizeUnixSeconds(value: number): number {
  return value > 1_000_000_000_000 ? Math.floor(value / 1000) : value;
}

function mapSearchItem(
  item: SearchNomorMutasiApiItem
): SearchNomorMutasiRowData {
  const remainingAmount = Number.isFinite(item.remaining_amount)
    ? item.remaining_amount
    : 0;

  return {
    mutationId: item.id,
    mutationDate: item.mutation_date
      ? unixToHumanDate(normalizeUnixSeconds(item.mutation_date), 'd MMM yy')
      : '-',
    mutationValue: formatCurrencyIdr(item.amount),
    mutationNo: item.mutation_no || '-',
    bank: item.bank_name || '-',
    memoLine: item.memo_line || '-',
    remark: item.remark || item.usage_summary || '-',
    remaining: formatCurrencyIdr(item.remaining_amount),
    remainingAmount,
    isFullyUsed: item.is_fully_used || remainingAmount <= 0,
  };
}

export function SearchNomorMutasiModal({
  isOpen,
  onClose,
  lhcId,
  lhcNo,
  rowData,
  onMutationSelected,
  title = 'Cari Nomor Mutasi',
  submitLabel = 'Gunakan Mutasi',
  showUsedMutations = false,
}: SearchNomorMutasiModalProps) {
  const toast = useToast();
  const methods = useForm<{ mutationDate: string }>({
    defaultValues: { mutationDate: '' },
  });
  const [searchValue, setSearchValue] = useState('');
  const [memoLineSearchValue, setMemoLineSearchValue] = useState('');
  const [mutationAmount, setMutationAmount] = useState('');
  const [searchError, setSearchError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [rows, setRows] = useState<SearchNomorMutasiRowData[]>([]);
  const [selectedRows, setSelectedRows] = useState<SearchNomorMutasiRowData[]>(
    []
  );
  const [isUsingMutation, setIsUsingMutation] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedMutation, setSelectedMutation] =
    useState<SearchNomorMutasiRowData | null>(null);
  const lastSearchRef = useRef<{
    mutationNo: string;
    memoLine: string;
  }>({ mutationNo: '', memoLine: '' });
  const selectedMutationForDetailRef = useRef<SearchNomorMutasiRowData | null>(
    null
  );

  const searchMutation = useSearchLHCItemMutationsMutation({
    onError: (error) => {
      const lastSearch = lastSearchRef.current;
      captureCollectionMutationError({
        error,
        queryKey: 'collection-search-mutasi',
        operation: 'search_lhc_item_mutations',
        context: {
          lhc_no: lhcNo,
          lhc_id: lhcId,
          mutation_no: lastSearch.mutationNo || null,
          mutation_id: null,
          ...buildRowDebugContext(rowData),
          search_mutation_no: lastSearch.mutationNo || null,
          search_memo_line: lastSearch.memoLine || null,
        },
      });
      showToast(
        toast,
        'error',
        getApiErrorMessage(error, 'Gagal mencari nomor mutasi', {
          includeStatusCode: true,
        })
      );
    },
  });
  const mutationDetailMutation = useCollectionMutationDetailMutation({
    onError: (error) => {
      const mutation = selectedMutationForDetailRef.current;
      captureCollectionMutationError({
        error,
        queryKey: 'collection-mutation-detail',
        operation: 'get_mutation_detail',
        context: {
          lhc_no: lhcNo,
          lhc_id: lhcId,
          mutation_no: mutation?.mutationNo ?? null,
          mutation_id: mutation?.mutationId ?? null,
          ...buildRowDebugContext(rowData),
        },
      });
      showToast(
        toast,
        'error',
        getApiErrorMessage(error, 'Gagal memuat detail mutasi', {
          includeStatusCode: true,
        })
      );
    },
  });
  const mutationDate = methods.watch('mutationDate');

  const resetAndClose = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedMutation(null);
    selectedMutationForDetailRef.current = null;
    setSearchValue('');
    setMemoLineSearchValue('');
    methods.reset({ mutationDate: '' });
    setMutationAmount('');
    setSearchError('');
    setHasSearched(false);
    setRows([]);
    setSelectedRows([]);
    onClose();
  }, [methods, onClose]);

  const handleSearch = useCallback(async () => {
    const trimmedNomor = searchValue.trim();
    const trimmedMemoLine = memoLineSearchValue.trim();

    if (trimmedNomor.length > 0 && trimmedNomor.length < MIN_SEARCH_LENGTH) {
      setSearchError('Nomor minimal 3 karakter untuk pencarian.');
      return;
    }

    if (
      trimmedMemoLine.length > 0 &&
      trimmedMemoLine.length < MIN_SEARCH_LENGTH
    ) {
      setSearchError('Memo line minimal 3 karakter untuk pencarian.');
      return;
    }

    setSearchError('');
    if (!rowData?.rowId) {
      showToast(
        toast,
        'error',
        'Data invoice tidak valid untuk pencarian mutasi'
      );
      return;
    }

    lastSearchRef.current = {
      mutationNo: trimmedNomor,
      memoLine: trimmedMemoLine,
    };

    const mutationDateUnix = parseDateToUnixSeconds(mutationDate);
    const amount = parseAmountToNumber(mutationAmount);

    try {
      const result = await searchMutation.mutateAsync({
        lhcId,
        itemId: rowData.rowId,
        params: {
          ...(trimmedNomor ? { mutation_no: trimmedNomor } : {}),
          ...(trimmedMemoLine ? { search_query: trimmedMemoLine } : {}),
          ...(mutationDateUnix ? { mutation_date: mutationDateUnix } : {}),
          ...(typeof amount === 'number'
            ? { amount, amount_tolerance_pct: 0 }
            : {}),
        },
      });

      setRows(result.data.map(mapSearchItem));
      setHasSearched(true);
      setSelectedRows([]);
    } catch {
      // Toast + Sentry are handled in searchMutation.onError
    }
  }, [
    lhcId,
    memoLineSearchValue,
    mutationAmount,
    mutationDate,
    rowData?.rowId,
    searchMutation,
    searchValue,
    toast,
  ]);

  const handleSelectionChanged = useCallback(
    (event: { api: { getSelectedRows: () => SearchNomorMutasiRowData[] } }) => {
      setSelectedRows(event.api.getSelectedRows());
    },
    []
  );

  const handleUseMutation = useCallback(async () => {
    if (!selectedRows.length) return;

    const selectedMutationRow = selectedRows[0];
    const invoiceAmount = parseAmountToNumber(rowData?.invoiceValue ?? '');
    const remainingAmount = selectedMutationRow.remainingAmount;

    if (typeof invoiceAmount !== 'number') {
      showToast(toast, 'error', 'Nominal faktur tidak valid');
      return;
    }

    if (!Number.isFinite(remainingAmount)) {
      showToast(toast, 'error', 'Nominal mutasi tidak valid');
      return;
    }

    if (selectedMutationRow.isFullyUsed || remainingAmount <= 0) {
      showToast(toast, 'error', 'Mutasi sudah habis digunakan');
      return;
    }

    const amountApplied = Math.min(invoiceAmount, remainingAmount);

    if (amountApplied <= 0) {
      showToast(toast, 'error', 'Nominal alokasi tidak valid');
      return;
    }

    const payload: SearchNomorMutasiSelectionPayload = {
      bankMutationId: selectedMutationRow.mutationId,
      mutationNo: selectedMutationRow.mutationNo,
      amountApplied,
    };

    try {
      setIsUsingMutation(true);
      await onMutationSelected?.(payload);
      showToast(toast, 'success', 'Mutasi berhasil digunakan');
      resetAndClose();
    } catch (error) {
      captureCollectionMutationError({
        error,
        queryKey: 'collection-use-mutation',
        operation: 'use_mutation_allocation',
        context: {
          lhc_no: lhcNo,
          lhc_id: lhcId,
          mutation_no: selectedMutationRow.mutationNo,
          mutation_id: selectedMutationRow.mutationId,
          ...buildRowDebugContext(rowData),
          amount_applied: amountApplied,
        },
      });
      showToast(
        toast,
        'error',
        getApiErrorMessage(error, 'Gagal menggunakan mutasi', {
          includeStatusCode: true,
        })
      );
    } finally {
      setIsUsingMutation(false);
    }
  }, [
    lhcId,
    lhcNo,
    onMutationSelected,
    resetAndClose,
    rowData,
    selectedRows,
    toast,
  ]);

  const handleOpenDrawer = useCallback(
    (mutation: SearchNomorMutasiRowData) => {
      if (!rowData?.orderMetaId) {
        showToast(toast, 'error', 'Data order tidak valid untuk detail mutasi');
        return;
      }

      selectedMutationForDetailRef.current = mutation;
      setSelectedMutation(mutation);
      setIsDrawerOpen(true);
      mutationDetailMutation.mutate({
        mutationId: mutation.mutationId,
        orderMetaId: rowData.orderMetaId,
      });
    },
    [mutationDetailMutation, rowData?.orderMetaId, toast]
  );

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedMutation(null);
    selectedMutationForDetailRef.current = null;
    mutationDetailMutation.reset();
  }, [mutationDetailMutation]);

  const columnDefs: ColDef<SearchNomorMutasiRowData>[] = [
    {
      colId: 'selection',
      headerName: '',
      width: 42,
      minWidth: 42,
      maxWidth: 42,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      pinned: 'left',
      sortable: false,
      resizable: false,
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
    { field: 'mutationDate', headerName: 'Tgl. Mutasi', width: 110 },
    { field: 'mutationValue', headerName: 'Nilai Mutasi', width: 130 },
    { field: 'mutationNo', headerName: 'Nomor Mutasi', width: 130 },
    { field: 'bank', headerName: 'Bank', width: 90 },
    {
      field: 'memoLine',
      headerName: 'Memo Line',
      minWidth: 260,
      flex: 2,
      resizable: true,
      tooltipField: 'memoLine',
    },
    {
      field: 'remark',
      headerName: 'Remark',
      minWidth: 170,
      flex: 1,
      resizable: true,
      tooltipField: 'remark',
    },
    { field: 'remaining', headerName: 'Sisa', width: 100 },
    {
      colId: 'detail',
      headerName: '',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      resizable: false,
      cellRenderer: (params: { data: SearchNomorMutasiRowData }) => (
        <Center
          height="100%"
          cursor="pointer"
          aria-label={`Lihat detail mutasi ${params.data.mutationNo}`}
          onClick={() => handleOpenDrawer(params.data)}
        >
          <Icon as={ChevronRight} color="#0000A3" boxSize={5} />
        </Center>
      ),
    },
  ];

  const resultRows = hasSearched ? rows : [];
  const rowHeight = 42;
  const headerHeight = 48;
  const maxTableHeight = headerHeight + 5 * rowHeight;
  const dynamicHeight = resultRows.length
    ? `${Math.min(
        headerHeight + resultRows.length * rowHeight + 2,
        maxTableHeight
      )}px`
    : '46px';

  const showEmptySearchState =
    hasSearched && resultRows.length === 0 && !searchMutation.isPending;
  const showPreSearchState = !hasSearched;

  return (
    <>
      <Modal
        title={title}
        name="search-nomor-mutasi-modal"
        isOpen={isOpen}
        onClose={resetAndClose}
        isCentered
        hideAction
        scrollBehavior="outside"
        closeOnOverlayClick={!isDrawerOpen}
        modalContentProps={{
          w: 'calc(100vw - 32px)',
          maxW: 'calc(100vw - 32px)',
          borderRadius: '16px',
        }}
        modalBodyProps={{ pb: 6 }}
        modalHeaderProps={{
          fontFamily: 'heading',
          fontWeight: 500,
          fontSize: '20px',
          lineHeight: '100%',
          letterSpacing: '0%',
          color: '#373F41',
        }}
      >
        <MutationContextSummary
          rowData={rowData}
          showUsedMutations={showUsedMutations}
        />

        <MutationSearchFilters
          searchValue={searchValue}
          memoLineSearchValue={memoLineSearchValue}
          mutationAmount={mutationAmount}
          searchError={searchError}
          isLoading={searchMutation.isPending}
          methods={methods}
          onSearchValueChange={setSearchValue}
          onMemoLineSearchValueChange={setMemoLineSearchValue}
          onMutationAmountChange={(value) =>
            setMutationAmount(formatDisplayAmount(value))
          }
          onSearch={() => {
            void handleSearch();
          }}
          onClearSearch={() => {
            setSearchValue('');
            setSearchError('');
          }}
          onClearMemoLineSearch={() => {
            setMemoLineSearchValue('');
          }}
        />

        <CollectionSearchResultsSection<SearchNomorMutasiRowData>
          columnDefs={columnDefs}
          rowData={resultRows}
          isLoading={searchMutation.isPending}
          height={dynamicHeight}
          minHeight={dynamicHeight}
          gridOptions={{
            rowSelection: 'multiple',
            onSelectionChanged: handleSelectionChanged,
            isRowSelectable: (node) =>
              !node.data?.isFullyUsed && (node.data?.remainingAmount ?? 0) > 0,
            suppressNoRowsOverlay: true,
            defaultColDef: {
              sortable: false,
              resizable: true,
            },
          }}
          sectionSx={{
            '& .ag-theme-diamond .ag-selection-checkbox': {
              margin: '0 !important',
            },
          }}
          emptyState={
            showEmptySearchState ? (
              <Center py={12}>
                <VStack spacing={4}>
                  <Text
                    fontWeight={500}
                    fontSize="20px"
                    lineHeight="100%"
                    letterSpacing="0%"
                    textAlign="center"
                    color="#000000"
                  >
                    Nomor Mutasi tidak ditemukan
                  </Text>
                  <Text
                    fontWeight={400}
                    fontSize="16px"
                    lineHeight="24px"
                    letterSpacing="0%"
                    textAlign="center"
                    color="#8E8E93"
                  >
                    Silakan cari nomor mutasi lain
                  </Text>
                </VStack>
              </Center>
            ) : null
          }
          preSearchState={
            showPreSearchState ? (
              <Center py={8}>
                <Text
                  fontWeight={500}
                  fontSize="16px"
                  lineHeight="100%"
                  letterSpacing="0%"
                  color="#8E8E93"
                >
                  Silahkan Cari Data Mutasi
                </Text>
              </Center>
            ) : null
          }
        />

        <Box display="flex" justifyContent="flex-end" mt={6}>
          <ChakraButton
            bgColor="#0000A3"
            color="white"
            _hover={{ bgColor: '#00008A' }}
            onClick={() => {
              void handleUseMutation();
            }}
            isLoading={isUsingMutation}
            isDisabled={!selectedRows.length || isUsingMutation}
          >
            {submitLabel}
          </ChakraButton>
        </Box>
      </Modal>

      <MutationDetailDrawer
        isOpen={isDrawerOpen}
        selectedMutation={selectedMutation}
        rowData={rowData}
        mutationDetailData={mutationDetailMutation.data ?? null}
        isLoading={mutationDetailMutation.isPending}
        onClose={handleCloseDrawer}
      />
    </>
  );
}
